import React from 'react';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../../common/SafeIcon';
import { format } from 'date-fns';
import { useProjects } from '../../context/ProjectContext';

const ListView = ({ projects, onEdit, onArchive, onDelete }) => {
  const { signOffProject } = useProjects();

  const handleSignOff = (id) => {
    const signer = window.prompt("Enter name of person signing off:");
    if (signer) {
      signOffProject(id, signer);
    }
  };

  return (
    <div className="bg-white dark:bg-gray-800 rounded-2xl border border-gray-100 dark:border-gray-700 overflow-hidden shadow-sm">
      <div className="overflow-x-auto">
        <table className="w-full text-left">
          <thead>
            <tr className="bg-gray-50 dark:bg-gray-900 border-b dark:border-gray-700">
              <th className="px-6 py-4 text-xs font-semibold text-gray-500 uppercase tracking-wider">Project</th>
              <th className="px-6 py-4 text-xs font-semibold text-gray-500 uppercase tracking-wider">Dates</th>
              <th className="px-6 py-4 text-xs font-semibold text-gray-500 uppercase tracking-wider">Info</th>
              <th className="px-6 py-4 text-xs font-semibold text-gray-500 uppercase tracking-wider">Status</th>
              <th className="px-6 py-4 text-xs font-semibold text-gray-500 uppercase tracking-wider text-right">Actions</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100 dark:divide-gray-700">
            {projects.map((project) => (
              <tr key={project.id} className="hover:bg-gray-50 dark:hover:bg-gray-700/50 transition">
                <td className="px-6 py-4">
                  <div className="flex items-center gap-3">
                    <div className={`w-2 h-10 rounded-full flex-shrink-0 ${project.urgency === 'Critical' ? 'bg-red-500' : project.urgency === 'High' ? 'bg-orange-500' : project.urgency === 'Medium' ? 'bg-blue-500' : 'bg-green-500'}`}></div>
                    <div>
                      <div className="flex items-center gap-2">
                        <div className="font-semibold text-gray-900 dark:text-white">{project.title}</div>
                        {project.signed_off && (
                          <SafeIcon icon={FiIcons.FiCheckCircle} className="text-green-500 text-sm" title={`Signed off by ${project.signed_off_by}`} />
                        )}
                      </div>
                      <div className="text-sm text-gray-500 dark:text-gray-400">
                        {project.stakeholder} {project.signed_off && <span className="text-[10px] ml-2 bg-green-50 dark:bg-green-900/30 text-green-600 px-1.5 py-0.5 rounded">APPROVED</span>}
                      </div>
                    </div>
                  </div>
                </td>
                <td className="px-6 py-4 text-sm text-gray-600 dark:text-gray-300">
                  <div className="flex flex-col gap-1">
                    <span className="flex items-center gap-2">
                      <SafeIcon icon={FiIcons.FiPlay} className="text-xs text-green-500" />
                      {project.edit_start_date ? format(new Date(project.edit_start_date), 'MMM d') : '-'}
                    </span>
                    <span className="flex items-center gap-2 font-medium">
                      <SafeIcon icon={FiIcons.FiFlag} className="text-xs text-red-500" />
                      {project.delivery_date ? format(new Date(project.delivery_date), 'MMM d') : '-'}
                    </span>
                  </div>
                </td>
                <td className="px-6 py-4">
                  <div className="flex gap-2 flex-wrap">
                    <span className="px-2 py-1 text-xs rounded-md bg-indigo-50 dark:bg-indigo-900/30 text-indigo-700 dark:text-indigo-300 border border-indigo-100 dark:border-indigo-800">
                      {project.department}
                    </span>
                    <span className="px-2 py-1 text-xs rounded-md bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-300">
                      {project.type}
                    </span>
                    <span className="px-2 py-1 text-xs rounded-md bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-300">
                      {project.version}
                    </span>
                  </div>
                </td>
                <td className="px-6 py-4">
                  <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${project.status === 'Complete' ? 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400' : project.status === 'Sent for review' ? 'bg-purple-100 text-purple-800 dark:bg-purple-900/30 dark:text-purple-400' : project.status === 'Editing' ? 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-400' : 'bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-400'}`}>
                    {project.status}
                  </span>
                </td>
                <td className="px-6 py-4 text-right">
                  <div className="flex items-center justify-end gap-2">
                    {project.review_link && (
                      <a href={project.review_link} target="_blank" rel="noopener noreferrer" className="p-2 text-gray-400 hover:text-indigo-600 transition" title="Review Link">
                        <SafeIcon icon={FiIcons.FiExternalLink} />
                      </a>
                    )}
                    {!project.signed_off && (
                      <button onClick={() => handleSignOff(project.id)} className="p-2 text-gray-400 hover:text-green-600 transition" title="Sign Off Project">
                        <SafeIcon icon={FiIcons.FiCheckSquare} />
                      </button>
                    )}
                    <button onClick={() => onEdit(project)} className="p-2 text-gray-400 hover:text-blue-600 transition" title="Edit Project">
                      <SafeIcon icon={FiIcons.FiEdit2} />
                    </button>
                    {onArchive && (
                      <button onClick={() => onArchive(project.id)} className="p-2 text-gray-400 hover:text-purple-600 transition" title="Archive Project">
                        <SafeIcon icon={FiIcons.FiArchive} />
                      </button>
                    )}
                    <button onClick={() => onDelete(project.id)} className="p-2 text-gray-400 hover:text-red-500 transition" title="Delete Project">
                      <SafeIcon icon={FiIcons.FiTrash2} />
                    </button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default ListView;